// Chapter Management Component for Admin Dashboard
import React, { useState, useEffect } from 'react';
import ConfirmDialog from './confirmdialog';
import { API_BASE } from '../services/api.js';

const ChapterManagement = ({ onBack }) => {
  const [chapters, setChapters] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingChapter, setEditingChapter] = useState(null);
  const [selectedSubject, setSelectedSubject] = useState('');
  const [showConfirm, setShowConfirm] = useState(false);
  const [deleteChapterId, setDeleteChapterId] = useState(null);
  
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    subject_id: ''
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      // Load subjects first
      const subjectsResponse = await fetch(`${API_BASE}/subjects/`);
      const subjectsData = await subjectsResponse.json();
      setSubjects(subjectsData);

      // Load all chapters
      const chaptersResponse = await fetch(`${API_BASE}/chapters/`);
      const chaptersData = await chaptersResponse.json();
      setChapters(chaptersData);

      setLoading(false);
    } catch (error) {
      setError('Failed to load data');
      setLoading(false);
    }
  };

  const loadChaptersBySubject = async (subjectId) => {
    if (!subjectId) {
      loadData();
      return;
    }

    try {
      const response = await fetch(`${API_BASE}/chapters/?subject_id=${subjectId}`);
      const data = await response.json();
      setChapters(data);
    } catch (error) {
      setError('Failed to load chapters');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      const url = editingChapter 
        ? `${API_BASE}/chapters/${editingChapter.chapter_id}`
        : `${API_BASE}/chapters/`;
      
      const method = editingChapter ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formData,
          subject_id: parseInt(formData.subject_id)
        })
      });

      if (response.ok) {
        setSuccess(editingChapter ? 'Chapter updated successfully' : 'Chapter created successfully');
        setFormData({ name: '', description: '', subject_id: '' });
        setShowAddForm(false);
        setEditingChapter(null);
        
        // Reload chapters based on current filter
        if (selectedSubject) {
          loadChaptersBySubject(selectedSubject);
        } else {
          loadData();
        }
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Operation failed');
      }
    } catch (error) {
      setError('Network error');
    }
  };

  const handleEdit = (chapter) => {
    setEditingChapter(chapter);
    setFormData({
      name: chapter.name,
      description: chapter.description || '',
      subject_id: chapter.subject_id.toString()
    });
    setShowAddForm(true);
  }; 

  const handleDelete = async (chapterId) => {
    setDeleteChapterId(chapterId);
    setShowConfirm(true);
  };

  const confirmDelete = async () => {
    try {
      const response = await fetch(`${API_BASE}/chapters/${deleteChapterId}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        setSuccess('Chapter deleted successfully');
        if (selectedSubject) {
          loadChaptersBySubject(selectedSubject);
        } else {
          loadData();
        }
      } else {
        setError('Failed to delete chapter. It may contain topics or other content.');
      }
    } catch (error) {
      setError('Network error');
    } finally {
      setShowConfirm(false);
      setDeleteChapterId(null);
    }
  };

  const cancelDelete = () => {
    setShowConfirm(false);
    setDeleteChapterId(null);
  };

  const cancelForm = () => {
    setShowAddForm(false);
    setEditingChapter(null);
    setFormData({ name: '', description: '', subject_id: '' });
  };

  const getSubjectName = (subjectId) => {
    const subject = subjects.find(s => s.subject_id === subjectId);
    return subject ? subject.name : 'Unknown';
  };

  if (loading) {
    return (
      <div className="admin-loading">
        <div className="loading-spinner"></div>
        <p>Loading chapters...</p>
      </div>
    );
  }

  return (
    <div className="chapter-management">
      {/* Header with Back Button */}
      <div className="management-header">
        <div className="header-left">
          <button className="back-btn" onClick={onBack}>
            ← Back to Dashboard
          </button>
          <h2>Chapter Management</h2>
        </div>
        <button 
          className="add-btn"
          onClick={() => setShowAddForm(!showAddForm)}
        >
          {showAddForm ? 'Cancel' : '+ Add Chapter'}
        </button>
      </div>

      {/* Alerts */}
      {error && (
        <div className="alert alert-error">
          {error}
          <button onClick={() => setError('')}>×</button>
        </div>
      )}
      
      {success && (
        <div className="alert alert-success">
          {success}
          <button onClick={() => setSuccess('')}>×</button>
        </div>
      )}

      {/* Filter by Subject */}
      <div className="filter-section">
        <div className="form-group">
          <label>Filter by Subject:</label>
          <select
            value={selectedSubject}
            onChange={(e) => {
              setSelectedSubject(e.target.value);
              loadChaptersBySubject(e.target.value);
            }}
            className="filter-select"
          >
            <option value="">All Subjects</option>
            {subjects.map(subject => (
              <option key={subject.subject_id} value={subject.subject_id}>
                {subject.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Add/Edit Chapter Form */}
      {showAddForm && (
        <div className="add-form-container">
          <h3>{editingChapter ? 'Edit Chapter' : 'Add New Chapter'}</h3>
          <form onSubmit={handleSubmit} className="add-form">
            <div className="form-row">
              <div className="form-group">
                <label>Chapter Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  placeholder="e.g., Introduction to Algebra, Laws of Motion"
                  required
                />
              </div>
              <div className="form-group">
                <label>Subject *</label>
                <select
                  value={formData.subject_id}
                  onChange={(e) => setFormData({...formData, subject_id: e.target.value})}
                  required
                >
                  <option value="">Select Subject</option>
                  {subjects.map(subject => (
                    <option key={subject.subject_id} value={subject.subject_id}>
                      {subject.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="form-group">
              <label>Description</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                placeholder="Brief description of the chapter (optional)"
                rows="3"
              />
            </div>
            <div className="form-actions">
              <button type="submit" className="submit-btn">
                {editingChapter ? 'Update Chapter' : 'Create Chapter'}
              </button>
              <button type="button" className="cancel-btn" onClick={cancelForm}>
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Chapters Grid */}
      <div className="chapters-container">
        <h3>
          {selectedSubject ? 
            `Chapters in ${getSubjectName(parseInt(selectedSubject))} (${chapters.length})` : 
            `All Chapters (${chapters.length})`
          }
        </h3>
        
        {chapters.length === 0 ? (
          <div className="no-data">
            <p>
              {selectedSubject ? 
                'No chapters found for this subject. Add your first chapter!' : 
                'No chapters found. Add your first chapter!'
              }
            </p>
          </div>
        ) : (
          <div className="chapters-grid">
            {chapters.map(chapter => (
              <div key={chapter.chapter_id} className="chapter-card">
                <div className="card-header">
                  <h4>{chapter.name}</h4>
                  <div className="card-actions">
                    <button 
                      className="action-btn edit"
                      onClick={() => handleEdit(chapter)}
                      title="Edit"
                    >
                      ✏️
                    </button>
                    <button 
                      className="action-btn delete"
                      onClick={() => handleDelete(chapter.chapter_id)}
                      title="Delete"
                    >
                      🗑️
                    </button>
                  </div>
                </div>
                
                <div className="card-content">
                  <div className="subject-badge">
                    {getSubjectName(chapter.subject_id)}
                  </div>
                  <p className="chapter-description">
                    {chapter.description || 'No description provided'}
                  </p>
                  <div className="card-meta">
                    <span className="chapter-id">ID: {chapter.chapter_id}</span>
                    <span className="created-date">
                      Created: {new Date(chapter.created_at).toLocaleDateString()}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Confirm Delete Dialog */}
      {showConfirm && (
        <div className="modal-overlay">
          <div className="modal">
            <ConfirmDialog
              message="Are you sure you want to delete this chapter? This will also delete all associated topics and content."
              onConfirm={confirmDelete}
              onCancel={cancelDelete}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default ChapterManagement;